import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { passwordRules } from '../../lib/validation';
import { OAuthButtons } from './OAuthButtons';
import { Mail, Lock, User, Loader2, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

export function SignUpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const failedRules = passwordRules.filter(r => !r.test(password));
    if (failedRules.length > 0) {
      setError('Password does not meet all requirements');
      return;
    }

    setLoading(true);

    const { error } = await signUp(email, password, name);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      setSuccess(true);
      // Auto-navigate to onboarding after short delay
      setTimeout(() => {
        navigate('/onboarding');
      }, 2000);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen gradient-mesh flex items-center justify-center p-5">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-emerald/20 flex items-center justify-center mx-auto mb-6 animate-scale-in">
            <CheckCircle className="w-10 h-10 text-emerald" />
          </div>
          <h1 className="font-display text-2xl font-bold text-text-primary mb-2">Account Created!</h1>
          <p className="text-text-muted mb-6">
            Check your email to verify your account, or you'll be redirected automatically.
          </p>
          <Link to="/login" className="text-gold hover:text-gold-light font-medium">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-mesh flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8 animate-slide-up">
          <img src="/logo-transparent.png" alt="Life OS" className="h-20 w-auto mx-auto mb-4" />
          <h1 className="font-display text-3xl font-bold text-gold">Join Life OS</h1>
          <p className="text-text-muted mt-2">Your AI-powered life operating system</p>
        </div>

        {/* Sign Up Form */}
        <div className="glass-card gold-border rounded-2xl p-6 animate-slide-up stagger-2">
          <h2 className="font-display text-xl font-semibold text-text-primary mb-6">Create Account</h2>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-ruby/10 border border-ruby/20 rounded-xl mb-4">
              <AlertCircle className="w-5 h-5 text-ruby flex-shrink-0" />
              <p className="text-sm text-ruby">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="signup-name" className="block text-xs text-text-muted uppercase tracking-wider mb-2">Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" aria-hidden="true" />
                <input
                  id="signup-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-elevated border border-subtle rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all"
                  placeholder="Your name"
                  required
                  autoComplete="name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="signup-email" className="block text-xs text-text-muted uppercase tracking-wider mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" aria-hidden="true" />
                <input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-elevated border border-subtle rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all"
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="signup-password" className="block text-xs text-text-muted uppercase tracking-wider mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" aria-hidden="true" />
                <input
                  id="signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-elevated border border-subtle rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all"
                  placeholder="At least 12 characters"
                  required
                  minLength={12}
                  autoComplete="new-password"
                />
              </div>
              {password.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {passwordRules.map((rule) => {
                    const passed = rule.test(password);
                    return (
                      <li key={rule.label} className="flex items-center gap-2 text-xs">
                        {passed ? (
                          <CheckCircle className="w-3.5 h-3.5 text-emerald flex-shrink-0" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-ruby flex-shrink-0" />
                        )}
                        <span className={passed ? 'text-emerald' : 'text-ruby'}>{rule.label}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div>
              <label htmlFor="signup-confirm-password" className="block text-xs text-text-muted uppercase tracking-wider mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" aria-hidden="true" />
                <input
                  id="signup-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-elevated border border-subtle rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:ring-1 focus:ring-gold/20 transition-all"
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gold py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-text-muted uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* OAuth Providers */}
          <OAuthButtons mode="signup" onError={setError} />
        </div>

        {/* Login Link */}
        <p className="text-center mt-6 text-text-muted animate-slide-up stagger-3">
          Already have an account?{' '}
          <Link to="/login" className="text-gold hover:text-gold-light font-medium transition-colors">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
